import React, { useState, useEffect } from 'react';
import { fetchAdminStudentsProgress, fetchAdminStudentDetail } from '../../services/api';
import Loading from '../Loading';
import Error from '../Error';

// Barevné prahy úspěšnosti: červená < 60 %, oranžová < 80 %, jinak zelená.
const successColor = (percentage) => {
  if (percentage === null || percentage === undefined) return '#6b7280';
  if (percentage < 60) return '#dc2626';
  if (percentage < 80) return '#f59e0b';
  return '#16a34a';
};

const EXERCISE_TYPE_LABELS = {
  vocabulary_fillin: 'Slovíčka',
  conversation_fillin: 'Doplňování',
  conversation_writing: 'Konverzace',
  speaking: 'Mluvení',
  writing: 'Psaní',
};

const formatDate = (dateString) => {
  if (!dateString) return '-';
  return new Date(dateString).toLocaleDateString('cs-CZ', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
};

const StudentProgressManager = () => {
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState(new Set());
  // user_id -> { loading, error, data } — detail se načítá až po rozbalení a cachuje se
  const [details, setDetails] = useState({});

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const data = await fetchAdminStudentsProgress();
        setStudents(data.students || []);
        setError(null);
      } catch (err) {
        console.error('Error fetching students progress:', err);
        setError('Nepodařilo se načíst přehled studentů.');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const toggleStudent = async (userId) => {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(userId)) {
        next.delete(userId);
      } else {
        next.add(userId);
      }
      return next;
    });

    // Lazy-load detailu při prvním rozbalení (pak už jen z cache).
    if (!details[userId]) {
      setDetails(prev => ({ ...prev, [userId]: { loading: true } }));
      try {
        const data = await fetchAdminStudentDetail(userId, 1);
        setDetails(prev => ({ ...prev, [userId]: { loading: false, data } }));
      } catch (err) {
        console.error('Error fetching student detail:', err);
        setDetails(prev => ({
          ...prev,
          [userId]: { loading: false, error: 'Nepodařilo se načíst detail studenta.' },
        }));
      }
    }
  };

  if (loading) {
    return <Loading />;
  }

  if (error) {
    return <Error message={error} />;
  }

  return (
    <div>
      <div style={{ marginBottom: '20px', display: 'flex', gap: '15px', alignItems: 'center' }}>
        <h2 style={{ margin: 0, color: '#2c3e50' }}>Postup studentů</h2>
        <span style={{ color: '#666', fontSize: '0.9rem' }}>
          {students.length} uživatelů
        </span>
      </div>

      {students.length === 0 ? (
        <p style={{ textAlign: 'center', color: '#666', fontStyle: 'italic', padding: '40px' }}>
          Žádní uživatelé nebyli nalezeni.
        </p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {students.map(student => {
            const detail = details[student.user_id];
            return (
              <div
                key={student.user_id}
                style={{ border: '1px solid #dee2e6', borderRadius: '8px', overflow: 'hidden' }}
              >
                {/* Hlavička studenta — rozbalovací */}
                <div
                  onClick={() => toggleStudent(student.user_id)}
                  style={{
                    padding: '15px 20px',
                    backgroundColor: expanded.has(student.user_id) ? '#e9ecef' : '#f8f9fa',
                    cursor: 'pointer',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: '10px',
                    transition: 'background-color 0.2s ease'
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <span style={{
                      fontSize: '1.2rem',
                      transition: 'transform 0.2s ease',
                      display: 'inline-block',
                      transform: expanded.has(student.user_id) ? 'rotate(90deg)' : 'rotate(0deg)'
                    }}>
                      ▶
                    </span>
                    <span style={{ fontWeight: '600', fontSize: '1.1rem', color: '#2c3e50' }}>
                      {student.username}
                    </span>
                    {student.is_admin && (
                      <span style={{
                        fontSize: '0.75rem', fontWeight: 600, padding: '2px 8px',
                        borderRadius: '12px', background: '#fce8d5', color: '#c0440a',
                      }}>
                        Admin
                      </span>
                    )}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '18px', fontSize: '0.88rem', color: '#374151' }}>
                    <span title="Zkušenosti a úroveň">
                      {student.total_xp} XP · úroveň {student.level} ({student.level_name})
                    </span>
                    <span title="Počet dokončených cvičení">
                      {student.attempts_count} pokusů
                    </span>
                    <span style={{ fontWeight: 600, color: successColor(student.success_rate) }} title="Průměrná úspěšnost">
                      {student.success_rate !== null && student.success_rate !== undefined
                        ? `${student.success_rate} %`
                        : '—'}
                    </span>
                    <span style={{ color: '#6b7280' }} title="Poslední aktivita">
                      {student.last_activity ? formatDate(student.last_activity) : 'bez aktivity'}
                    </span>
                  </div>
                </div>

                {/* Detail — kategorie + poslední pokusy */}
                {expanded.has(student.user_id) && (
                  <div style={{ padding: '15px 20px', backgroundColor: 'white' }}>
                    {detail?.loading && (
                      <p style={{ color: '#6b7280', margin: 0 }}>Načítám detail...</p>
                    )}
                    {detail?.error && (
                      <p style={{ color: '#dc2626', margin: 0 }}>{detail.error}</p>
                    )}
                    {detail?.data && (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                        {/* Kategorie od nejhorších */}
                        <div>
                          <h4 style={{ margin: '0 0 10px 0', color: '#495057', fontSize: '0.95rem' }}>
                            Úspěšnost podle gramatických kategorií (od nejhorších)
                          </h4>
                          {detail.data.categories.length === 0 ? (
                            <p style={{ color: '#6b7280', fontStyle: 'italic', margin: 0, fontSize: '0.88rem' }}>
                              Zatím žádná data podle kategorií.
                            </p>
                          ) : (
                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.88rem' }}>
                              <thead>
                                <tr style={{ backgroundColor: '#f1f3f5', textAlign: 'left' }}>
                                  <th style={{ padding: '8px 12px', borderBottom: '1px solid #dee2e6' }}>Kategorie</th>
                                  <th style={{ padding: '8px 12px', borderBottom: '1px solid #dee2e6', width: '120px' }}>Správně / celkem</th>
                                  <th style={{ padding: '8px 12px', borderBottom: '1px solid #dee2e6', width: '90px' }}>Úspěšnost</th>
                                </tr>
                              </thead>
                              <tbody>
                                {detail.data.categories.map((cat) => (
                                  <tr key={cat.category} style={{ borderBottom: '1px solid #f3f4f6' }}>
                                    <td style={{ padding: '8px 12px' }}>{cat.category}</td>
                                    <td style={{ padding: '8px 12px', color: '#6b7280' }}>
                                      {cat.correct} / {cat.total}
                                    </td>
                                    <td style={{ padding: '8px 12px', fontWeight: 600, color: successColor(cat.percentage) }}>
                                      {cat.percentage} %
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          )}
                        </div>

                        {/* Poslední pokusy */}
                        <div>
                          <h4 style={{ margin: '0 0 10px 0', color: '#495057', fontSize: '0.95rem' }}>
                            Posledních {detail.data.recent_attempts.length} pokusů
                          </h4>
                          {detail.data.recent_attempts.length === 0 ? (
                            <p style={{ color: '#6b7280', fontStyle: 'italic', margin: 0, fontSize: '0.88rem' }}>
                              Zatím žádné pokusy.
                            </p>
                          ) : (
                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.88rem' }}>
                              <thead>
                                <tr style={{ backgroundColor: '#f1f3f5', textAlign: 'left' }}>
                                  <th style={{ padding: '8px 12px', borderBottom: '1px solid #dee2e6' }}>Téma</th>
                                  <th style={{ padding: '8px 12px', borderBottom: '1px solid #dee2e6', width: '120px' }}>Cvičení</th>
                                  <th style={{ padding: '8px 12px', borderBottom: '1px solid #dee2e6', width: '90px' }}>Skóre</th>
                                  <th style={{ padding: '8px 12px', borderBottom: '1px solid #dee2e6', width: '90px' }}>Chyby</th>
                                  <th style={{ padding: '8px 12px', borderBottom: '1px solid #dee2e6', width: '140px' }}>Dokončeno</th>
                                </tr>
                              </thead>
                              <tbody>
                                {detail.data.recent_attempts.map((attempt, idx) => (
                                  <tr key={idx} style={{ borderBottom: '1px solid #f3f4f6' }}>
                                    <td style={{ padding: '8px 12px' }}>{attempt.topic}</td>
                                    <td style={{ padding: '8px 12px', color: '#6b7280' }}>
                                      {EXERCISE_TYPE_LABELS[attempt.exercise_type] || attempt.exercise_type}
                                    </td>
                                    <td style={{
                                      padding: '8px 12px', fontWeight: 600,
                                      color: successColor(attempt.max_score > 0 ? attempt.score / attempt.max_score * 100 : null),
                                    }}>
                                      {attempt.score} / {attempt.max_score}
                                    </td>
                                    <td style={{ padding: '8px 12px', color: '#6b7280' }}>
                                      {Array.isArray(attempt.errors) && attempt.errors.length > 0
                                        ? attempt.errors.length
                                        : '—'}
                                    </td>
                                    <td style={{ padding: '8px 12px', color: '#6b7280' }}>
                                      {formatDate(attempt.completed_at)}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default StudentProgressManager;
